
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Report Configuration Service
 * Uses backend API endpoints instead of direct Firestore access
 */
export class ReportConfigService {
  static COLLECTION_NAME = 'reportConfigs';

  /**
   * Get all report configurations for a specific context (master or facility)
   * @param {boolean} isMaster - Whether to fetch master configurations
   * @param {string} facilityId - Facility ID for facility-specific configs
   * @param {boolean} includeMasterFallback - Whether to include master configs as fallback
   * @returns {Promise<Array>} Array of report configurations
   */
  static async getConfigurations(isMaster = false, facilityId = null, includeMasterFallback = true) {
    try {
      console.log('Getting report configurations via API:', { isMaster, facilityId, includeMasterFallback });
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('isMaster', isMaster.toString());
      if (facilityId) {
        params.append('facilityId', facilityId);
      }
      params.append('includeMasterFallback', includeMasterFallback.toString());
      
      const response = await axios.get(
        `${API_BASE}/admin/report-configs?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.configurations;
    } catch (error) {
      console.error('Error fetching configurations:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Get configurations grouped by report type and report key
   * @param {boolean} isMaster - Whether to fetch master configurations
   * @param {string} facilityId - Facility ID for facility-specific configs
   * @param {boolean} includeMasterFallback - Whether to include master configs as fallback for facility
   * @returns {Promise<Object>} Configurations grouped by type and key
   */
  static async getConfigurationsGrouped(isMaster = false, facilityId = null, includeMasterFallback = true) {
    try {
      console.log('Getting grouped report configurations via API:', { isMaster, facilityId, includeMasterFallback });
      
      const configurations = await this.getConfigurations(isMaster, facilityId, includeMasterFallback);
      
      const grouped = {};
      configurations.forEach(config => {
        if (!grouped[config.reportType]) {
          grouped[config.reportType] = {};
        }
        grouped[config.reportType][config.reportKey] = config;
      });

      return grouped;
    } catch (error) {
      console.error('Error fetching grouped configurations:', error);
      throw error;
    }
  }

  /**
   * Create a new report configuration
   * @param {Object} configData - Configuration data
   * @param {string} userId - ID of the user creating the config (optional - backend will use token)
   * @returns {Promise<string>} Created document ID
   */
  static async createConfiguration(configData, userId = null) {
    try {
      console.log('Creating report configuration via API:', configData);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/admin/report-configs`,
        configData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.configId;
    } catch (error) {
      console.error('Error creating configuration:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Update an existing report configuration
   * @param {string} configId - Configuration document ID
   * @param {Object} updateData - Data to update
   * @param {string} userId - ID of the user updating the config (optional - backend will use token)
   * @returns {Promise<void>}
   */
  static async updateConfiguration(configId, updateData, userId = null) {
    try {
      console.log('Updating report configuration via API:', configId, updateData);
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/admin/report-configs/${configId}`,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Error updating configuration:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Delete a report configuration
   * @param {string} configId - Configuration document ID
   * @returns {Promise<void>}
   */
  static async deleteConfiguration(configId) {
    try {
      console.log('Deleting report configuration via API:', configId);
      const token = await getAuthToken();
      
      await axios.delete(
        `${API_BASE}/admin/report-configs/${configId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Error deleting configuration:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Copy master configurations to a facility
   * @param {string} reportType - Type of reports to copy
   * @param {string} facilityId - Target facility ID
   * @param {string} userId - ID of the user performing the copy (optional - backend will use token)
   * @returns {Promise<Array>} Array of created configuration IDs
   */
  static async copyMasterToFacility(reportType, facilityId, userId = null) {
    try {
      console.log('Copying master configurations to facility via API:', reportType, facilityId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/admin/report-configs/copy-master`,
        { reportType, facilityId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.createdConfigIds;
    } catch (error) {
      console.error('Error copying master configurations:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Get enabled reports for a facility
   * @param {string} facilityId - Facility ID
   * @param {string} reportType - Optional report type filter
   * @returns {Promise<Array>} Array of enabled report configurations
   */
  static async getEnabledReports(facilityId, reportType = null) {
    try {
      console.log('Getting enabled reports via API:', facilityId, reportType);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('facilityId', facilityId);
      if (reportType) {
        params.append('reportType', reportType);
      }
      
      const response = await axios.get(
        `${API_BASE}/admin/report-configs/enabled?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.enabledReports;
    } catch (error) {
      console.error('Error fetching enabled reports:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Toggle report enabled status
   * @param {string} configId - Configuration document ID
   * @param {boolean} enabled - New enabled status
   * @param {string} userId - ID of the user making the change (optional - backend will use token)
   * @returns {Promise<void>}
   */
  static async toggleReportStatus(configId, enabled, userId = null) {
    try {
      console.log('Toggling report status via API:', configId, enabled);
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/admin/report-configs/${configId}/toggle`,
        { enabled },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Error toggling report status:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Get report configuration by type and key
   * @param {string} reportType - Report type
   * @param {string} reportKey - Report key
   * @param {boolean} isMaster - Whether to fetch master configuration
   * @param {string} facilityId - Facility ID for facility-specific config
   * @returns {Promise<Object|null>} Report configuration or null if not found
   */
  static async getConfigurationByKey(reportType, reportKey, isMaster = false, facilityId = null) {
    try {
      console.log('Getting configuration by key via API:', { reportType, reportKey, isMaster, facilityId });
      
      const configurations = await this.getConfigurations(isMaster, facilityId, false);
      
      const matchingConfig = configurations.find(config => 
        config.reportType === reportType && config.reportKey === reportKey
      );
      
      return matchingConfig || null;
    } catch (error) {
      console.error('Error fetching configuration by key:', error);
      throw error;
    }
  }

  // Additional helper methods for enhanced functionality

  /**
   * Get all report types for a facility
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Array>} Array of available report types
   */
  static async getAvailableReportTypes(facilityId) {
    try {
      console.log('Getting available report types via API:', facilityId);
      
      const configurations = await this.getConfigurations(false, facilityId, true);
      
      const reportTypes = [...new Set(configurations.map(config => config.reportType))];
      
      return reportTypes.sort();
    } catch (error) {
      console.error('Error getting available report types:', error);
      throw error;
    }
  }

  /**
   * Get report configuration statistics
   * @param {string} facilityId - Facility ID (optional)
   * @returns {Promise<Object>} Configuration statistics
   */
  static async getConfigurationStats(facilityId = null) {
    try {
      console.log('Getting configuration statistics via API:', facilityId);
      
      if (facilityId) {
        const configurations = await this.getConfigurations(false, facilityId, false);
        const masterConfigurations = await this.getConfigurations(true, null, false);
        
        return {
          totalConfigurations: configurations.length,
          enabledConfigurations: configurations.filter(c => c.enabled).length,
          disabledConfigurations: configurations.filter(c => !c.enabled).length,
          inheritedFromMaster: configurations.filter(c => c.inheritedFromMaster).length,
          customConfigurations: configurations.filter(c => !c.inheritedFromMaster).length,
          masterConfigurations: masterConfigurations.length,
          reportTypes: [...new Set(configurations.map(c => c.reportType))].length,
          facilityId
        };
      } else {
        const masterConfigurations = await this.getConfigurations(true, null, false);
        
        return {
          totalMasterConfigurations: masterConfigurations.length,
          enabledMasterConfigurations: masterConfigurations.filter(c => c.enabled).length,
          disabledMasterConfigurations: masterConfigurations.filter(c => !c.enabled).length,
          reportTypes: [...new Set(masterConfigurations.map(c => c.reportType))].length,
          isMaster: true
        };
      }
    } catch (error) {
      console.error('Error getting configuration statistics:', error);
      throw error;
    }
  }

  /**
   * Search report configurations
   * @param {string} searchTerm - Search term
   * @param {Object} filters - Additional filters
   * @returns {Promise<Array>} Array of matching configurations
   */
  static async searchConfigurations(searchTerm, filters = {}) {
    try {
      console.log('Searching report configurations via API:', searchTerm, filters);
      
      const { isMaster = false, facilityId = null, includeMasterFallback = true } = filters;
      const configurations = await this.getConfigurations(isMaster, facilityId, includeMasterFallback);
      
      if (!searchTerm || searchTerm.trim() === '') {
        return configurations;
      }
      
      const lowerSearchTerm = searchTerm.toLowerCase();
      return configurations.filter(config => 
        (config.reportType && config.reportType.toLowerCase().includes(lowerSearchTerm)) ||
        (config.reportKey && config.reportKey.toLowerCase().includes(lowerSearchTerm)) ||
        (config.settings?.title && config.settings.title.toLowerCase().includes(lowerSearchTerm)) ||
        (config.settings?.description && config.settings.description.toLowerCase().includes(lowerSearchTerm))
      );
    } catch (error) {
      console.error('Error searching configurations:', error);
      throw error;
    }
  }

  /**
   * Bulk update report configurations
   * @param {Array} updates - Array of updates with configId and updateData
   * @returns {Promise<Object>} Summary of the bulk operation
   */
  static async bulkUpdateConfigurations(updates) {
    try {
      console.log('Bulk updating report configurations via API:', updates.length);
      
      const promises = updates.map(update => 
        this.updateConfiguration(update.configId, update.updateData)
      );
      
      const results = await Promise.allSettled(promises);
      
      const summary = {
        successful: 0,
        failed: 0,
        errors: []
      };
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          summary.successful++;
        } else {
          summary.failed++;
          summary.errors.push({
            configId: updates[index].configId,
            error: result.reason
          });
        }
      });
      
      return summary;
    } catch (error) {
      console.error('Error bulk updating configurations:', error);
      throw error;
    }
  }

  /**
   * Export report configurations
   * @param {Object} filters - Export filters
   * @param {string} format - Export format ('json' or 'csv')
   * @returns {Promise<string>} Exported data
   */
  static async exportConfigurations(filters = {}, format = 'json') {
    try {
      console.log('Exporting report configurations via API:', filters, format);
      
      const { isMaster = false, facilityId = null, includeMasterFallback = true } = filters;
      const configurations = await this.getConfigurations(isMaster, facilityId, includeMasterFallback);
      
      if (format === 'json') {
        return JSON.stringify(configurations, null, 2);
      } else if (format === 'csv') {
        // Convert to CSV format
        if (configurations.length === 0) {
          return '';
        }
        
        const headers = ['ID', 'Report Type', 'Report Key', 'Enabled', 'Master', 'Facility ID', 'Created At'];
        const rows = configurations.map(config => [
          config.id,
          config.reportType,
          config.reportKey,
          config.enabled,
          config.isMaster,
          config.facilityId || '',
          config.createdAt
        ]);
        
        const csvContent = [headers, ...rows]
          .map(row => row.map(cell => `"${cell}"`).join(','))
          .join('\n');
        
        return csvContent;
      } else {
        throw new Error('Unsupported export format. Use "json" or "csv".');
      }
    } catch (error) {
      console.error('Error exporting configurations:', error);
      throw error;
    }
  }

  /**
   * Import report configurations
   * @param {string} data - Configuration data to import (JSON format)
   * @param {Object} options - Import options
   * @returns {Promise<Object>} Summary of the import operation
   */
  static async importConfigurations(data, options = {}) {
    try {
      console.log('Importing report configurations via API:', options);
      
      const { overwrite = false, facilityId = null } = options;
      let configurations;
      
      try {
        configurations = JSON.parse(data);
      } catch {
        throw new Error('Invalid JSON format for import data');
      }
      
      if (!Array.isArray(configurations)) {
        throw new Error('Import data must be an array of configurations');
      }
      
      const summary = {
        total: configurations.length,
        created: 0,
        updated: 0,
        skipped: 0,
        errors: []
      };
      
      for (const config of configurations) {
        try {
          // Remove ID for new configurations
          const { id, createdAt, updatedAt, createdBy, updatedBy, ...configData } = config;
          
          // Override facility ID if provided
          if (facilityId && !configData.isMaster) {
            configData.facilityId = facilityId;
          }
          
          // Check if configuration already exists
          const existingConfig = await this.getConfigurationByKey(
            configData.reportType,
            configData.reportKey,
            configData.isMaster,
            configData.facilityId
          );
          
          if (existingConfig) {
            if (overwrite) {
              await this.updateConfiguration(existingConfig.id, configData);
              summary.updated++;
            } else {
              summary.skipped++;
            }
          } else {
            await this.createConfiguration(configData);
            summary.created++;
          }
        } catch (configError) {
          summary.errors.push({
            config: config,
            error: configError.message
          });
        }
      }
      
      return summary;
    } catch (error) {
      console.error('Error importing configurations:', error);
      throw error;
    }
  }

  /**
   * Validate report configuration
   * @param {Object} configData - Configuration data to validate
   * @returns {Promise<Object>} Validation result
   */
  static async validateConfiguration(configData) {
    try {
      console.log('Validating report configuration:', configData);
      
      const errors = [];
      const warnings = [];
      
      // Required field validation
      if (!configData.reportType) {
        errors.push('Report type is required');
      }
      
      if (!configData.reportKey) {
        errors.push('Report key is required');
      }
      
      if (typeof configData.enabled !== 'boolean') {
        errors.push('Enabled must be a boolean value');
      }
      
      if (typeof configData.isMaster !== 'boolean') {
        errors.push('isMaster must be a boolean value');
      }
      
      // Facility-specific validation
      if (!configData.isMaster && !configData.facilityId) {
        errors.push('Facility ID is required for non-master configurations');
      }
      
      // Settings validation
      if (configData.settings && typeof configData.settings !== 'object') {
        errors.push('Settings must be an object');
      }
      
      // Check for duplicate configurations
      if (configData.reportType && configData.reportKey) {
        try {
          const existingConfig = await this.getConfigurationByKey(
            configData.reportType,
            configData.reportKey,
            configData.isMaster,
            configData.facilityId
          );
          
          if (existingConfig) {
            warnings.push('A configuration with this report type and key already exists');
          }
        } catch {
          warnings.push('Could not check for duplicate configurations');
        }
      }
      
      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    } catch (error) {
      console.error('Error validating configuration:', error);
      return {
        isValid: false,
        errors: ['Validation failed: ' + error.message],
        warnings: []
      };
    }
  }
}

export default ReportConfigService;
